import { HourglassEmpty, UpdateRounded } from "@mui/icons-material";
import { isBefore } from "date-fns";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import { useMemo } from "react";
import CellRendererEditableLoanField from "../../../../_src_shared/agGrid/cellRenderer/CellRendererEditableLoanField";
import CellRendererLoanComments from "../../../../components/agGrid/cellRenderer/CellRendererLoanComments";
import CellRendererLoanName from "../../../../components/agGrid/cellRenderer/CellRendererLoanName";
import CellRendererStackedChip from "../../../../components/agGrid/cellRenderer/CellRendererStackedChip";
import CellRendererUserByEmail from "../../../../components/agGrid/cellRenderer/CellRendererUserByEmail";
import OpenInNewTabButton from "../../../../components/ui/buttons/OpenInNewTabButton";
import { selectOptionsLoanStatus } from "../../../../constants/selectOptions/selectOptionsLoanStatus";
import { selectOptionsPipelineType } from "../../../../constants/selectOptions/selectOptionsPipelineType";

export const useColumnDefsLoanPipelineTable = ({}) => {
  return useMemo(
    () => [
      { hide: true, headerName: "_id", field: "_id" },
      {
        headerName: "Loan #",
        field: "loanNumber",
        minWidth: 90,
        maxWidth: 90,
        width: 90,
        suppressAutoSize: true,
        suppressHeaderFilterButton: true,
        flex: 0,
        // hide: true,
        getQuickFilterText: (params) => {
          // console.log("getQuickFilterText", params);
          // Convert the number to string explicitly
          return params.value ? String(params.value) : "";
        },
        filter: "agTextColumnFilter",
        cellRenderer: (params) => (
          <OpenInNewTabButton linkIcon to={`/loan/${params?.data?.loanNumber}`}>
            {params?.data?.loanNumber}
          </OpenInNewTabButton>
        ),
        suppressFloatingFilterButton: true,
      },
      {
        headerName: "Created",
        field: "createDateTime",
        type: "date",
        minWidth: 105,
        maxWidth: 105,
        width: 105,
        suppressHeaderFilterButton: true,
        suppressFloatingFilterButton: true,
        wrapHeaderText: true,
        cellRenderer: (p) => (
          <CellRendererStackedChip
            icon={<HourglassEmpty className="thin-8" />}
            params={p}
          />
        ),
      },

      {
        headerName: "Updated",
        field: "updateDateTime",
        type: "date",
        minWidth: 110,
        maxWidth: 110,
        width: 110,
        wrapHeaderText: true,
        suppressHeaderFilterButton: true,
        cellRenderer: (p) => (
          <CellRendererStackedChip
            icon={<UpdateRounded className="thin-8" />}
            params={p}
          />
        ),
      },
      {
        headerName: "Loan Name",
        field: "loanName",
        minWidth: 280,
        width: 360,
        cellRenderer: CellRendererLoanName,
        suppressSizeToFit: true,
        valueGetter: (params) =>
          isNil(params?.data?.loanName)
            ? `Loan #${params?.data?.loanNumber}`
            : params?.data?.loanName,
        suppressAutoSize: true,
        suppressHeaderFilterButton: true,
        autoHeight: true,
        suppressFloatingFilterButton: true,
        // tooltipValueGetter: (params) => {
        //   return `Go to loan ${params?.data?.loanNumber}`;
        // },
        headerTooltip: "Go to loan",
        cellStyle: {
          cursor: "pointer",
        },
      },
      {
        field: "sync_loan_id",
        headerName: "External Loan Sync ID",
        hide: true,
      },
      {
        headerName: "Comments",
        field: "comments",
        suppressAutoSize: true,
        suppressSizeToFit: true,
        suppressHeaderFilterButton: true,
        width: 600,
        autoHeight: true,
        wrapText: true,
        suppressFloatingFilterButton: true,
        cellStyle: {
          paddingTop: "14px",
          paddingBottom: "14px",
          paddingLeft: 0,
          cursor: "pointer",
          // height: "100%",
        },

        cellRenderer: CellRendererLoanComments,
        cellClass: (params) => {
          if (isEmpty(params?.value)) {
            return "ag-cell-vertical-center";
          }
        },
        comparator: (valueA, valueB) => {
          // Check if arrays exist and have content
          const hasCommentsA =
            valueA && Array.isArray(valueA) && valueA.length > 0;
          const hasCommentsB =
            valueB && Array.isArray(valueB) && valueB.length > 0;

          // If neither has comments, consider them equal (maintain original order)
          if (!hasCommentsA && !hasCommentsB) return 0;

          // If only one has comments, push empty ones to the top (reversed)
          if (!hasCommentsA) return -1; // A (no comments) goes before B (has comments)
          if (!hasCommentsB) return 1; // B (no comments) goes before A (has comments)

          // Both have comments, compare by first comment's date
          const dateA = valueA[0].commentDateTime;
          const dateB = valueB[0].commentDateTime;

          // Convert to Date objects for proper comparison
          const parsedDateA = new Date(dateA);
          const parsedDateB = new Date(dateB);

          // For oldest first (reversed), use ascending order (A - B)
          return parsedDateA.getTime() - parsedDateB.getTime();
        },
      },
      { field: "tags", headerName: "Loan Tags", hide: true },
      {
        headerName: "Loan Status",
        field: "loanStatus",
        minWidth: 180,
        cellStyle: {
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
        },
        flex: 0,
        cellRenderer: CellRendererEditableLoanField,
        cellEditorParams: {
          values: selectOptionsLoanStatus || [],
        },
      },
      {
        headerName: "Pipeline",
        field: "pipeline",
        width: 140,
        flex: 0,
        cellRenderer: CellRendererEditableLoanField,
        cellEditorParams: {
          values: selectOptionsPipelineType || [],
        },
      },
      {
        headerName: "Salesperson",
        field: "salesperson",
        cellRenderer: (p) => <CellRendererUserByEmail {...p} stacked={true} />,
      },
      {
        headerName: "Referral Source",
        field: "referralSource",
        cellRenderer: CellRendererEditableLoanField,
        valueFormatter: (params) => params?.data?.referralSource || "None",
      },
      {
        headerName: "Expected Closing Date",
        field: "expectedClosingDate",
        type: "date",
        minWidth: 180,
        cellRenderer: CellRendererEditableLoanField,
        cellStyle: (p) => {
          const valueDate = p?.value ? new Date(p.value) : null;
          const isPastDate = valueDate && isBefore(valueDate, new Date());

          const closedStatuses = [
            "Closed",
            "Closed w/ Post Closing Conditions",
            "Balance Sheet",
            "Loan Sold",
            "Inactive",
            "Dead",
          ];

          const isClosedLoan = closedStatuses.includes(p?.data?.loanStatus);

          if (isPastDate) {
            // ✅ Closed loan → green
            if (isClosedLoan) {
              return {
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                background: "#e8f5e9ab", // light green
              };
            }

            // ❌ Active loan → red
            return {
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              background: "#ffebee6b",
            };
          }

          return {
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
          };
        },
      },
      {
        headerName: "Loan Product Type",
        field: "loanProductType",
      },
      {
        headerName: "Loan Purpose",
        field: "loanPurpose",
        flex: 0,
      },

      // -----------
      {
        headerName: "Total Holdback",
        field: "totalHoldback",
        type: "dollar",
      },
      {
        headerName: "Base Loan Amount",
        field: "baseLoanAmount",
        type: "dollar",
        cellRenderer: CellRendererEditableLoanField,
      },
      {
        headerName: "Total Loan Amount",
        field: "totalLoanAmount",
        type: "dollar",
      },
      {
        headerName: "Interest Rate",
        field: "interestRate",
        type: "percent",
      },
      {
        headerName: "Discount Points",
        field: "discountPoints",
        type: "percent",
      },
      {
        headerName: "Origination Points",
        field: "originationPoints",
        type: "percent",
      },
      {
        headerName: "Existing Loan In Default",
        field: "existingLoanInDefault",
        type: "boolean",
      },
      {
        field: "baseLoanToAsIsValue",
        type: "percent",
      },
      {
        field: "baseLoanToPurchasePrice",
        type: "percent",
      },
      {
        field: "totalLoanToArv",
        type: "percent",
      },
      {
        field: "totalArv",
        type: "dollar",
      },
      {
        field: "totalAsIsValue",
        type: "dollar",
      },
      {
        field: "totalConstructionBudget",
        type: "dollar",
      },
      {
        field: "totalCostBasis",
        type: "dollar",
      },
      {
        field: "totalPurchasePrice",
        type: "dollar",
      },
      {
        field: "closingWeek",
        // type: "dollar",
      },
      {
        field: "closingMonth",
        // type: "dollar",
      },
      {
        field: "closingQuarter",
        // type: "dollar",
      },
      // ----------- END

      {
        headerName: "Subject Properties",
        field: "subjectProperties",
        valueGetter: (params) => {
          const v = params.data?.subjectProperties;
          if (isEmpty(v) || isNil(v)) {
            return;
          }
          const val = v.map((item) =>
            isEmpty(item) || isNil(item?.address?.fullAddress)
              ? "N/A"
              : `${item.address.fullAddress}`,
          );
          return val;
        },
      },
      {
        headerName: "Borrowers",
        field: "borrowers",
        valueGetter: (params) => {
          const v = params.data?.borrowers;
          if (isEmpty(v) || isNil(v)) {
            return;
          }
          const val = v.map((item) =>
            isEmpty(item) || isNil(item?.firstName) || isNil(item?.lastName)
              ? "N/A"
              : `${item.firstName} ${item.lastName}`,
          );
          return val;
        },
      },
      {
        headerName: "Last Viewed",
        field: "lastViewDateTime",
        type: "date",
        minWidth: 200,
      },
      // ----------- Hidden
      { headerName: "Asset ID", field: "assetId", hide: true },
      {
        headerName: "Is Table Funding",
        field: "isTableFunding",
        hide: true,
        type: "boolean",
      },
      {
        headerName: "Is Balance Sheet Closing",
        field: "isBalanceSheetClosing",
        hide: true,
      },
      {
        headerName: "Milestone Status Changes",
        field: "milestoneStatusChanges",
        hide: true,
      },
      { headerName: "Processor", field: "processor", hide: true },
      { headerName: "Underwriter", field: "underwriter", hide: true },
      { headerName: "Loan Closer", field: "loanCloser", hide: true },
      { headerName: "Permitted Users", field: "permittedUsers", hide: true },
      { headerName: "Dutch Interest", field: "dutchInterest", hide: true },
      {
        headerName: "Net Funding",
        field: "netFunding",
        type: "dollar",
        hide: true,
      },
      {
        headerName: "Origination Date",
        hide: true,
        field: "originationDate",
        type: "date",
      },
      {
        hide: true,
        headerName: "Funded Date",
        field: "fundedDate",
        type: "date",
      },
      {
        hide: true,
        headerName: "Maturity Date",
        field: "maturityDate",
        type: "date",
      },
      {
        headerName: "Closing Scheduled",
        hide: true,
        field: "closingScheduled",
        type: "boolean",
      },
      {
        headerName: "Closing Scheduled Date",
        hide: true,
        field: "closingScheduledDate",
        type: "boolean",
      },
      { hide: true, headerName: "Loan Documents", field: "loanDocuments" },
    ],
    [],
  );
};

const columnDefConfigUserByEmail = () => {
  return [
    {
      headerName: "User Email",
      field: "userEmail",
      cellRenderer: "CellRendererUserByEmail",
    },
  ];
};
